import axios from "axios";
import { toastr } from "react-redux-toastr";
import { logger } from "./loggerHelper";
import { HTTP_STATUS } from "../config/constants";

const SENSITIVE_LOG_KEYS: string[] = [
  "access_token",
  "accessToken",
  "apiAccessPrivateKey",
  "app_secret",
  "app_token",
  "authorization",
  "code",
  "password",
  "refresh_token",
  "session_id",
  "token",
];

const REDACTED_VALUE = "REDACTED";

interface ErrorHandlerConfig {
  url?: string;
  method?: string;
  suppressToast?: boolean;
}

interface ErrorHandlerResponse {
  status?: number;
  data?: {
    message?: string;
  };
}

interface ErrorHandlerError {
  config?: ErrorHandlerConfig;
  response?: ErrorHandlerResponse;
  request?: unknown;
  message?: string;
  code?: string;
}

const isSensitiveKey = (key = ""): boolean =>
  SENSITIVE_LOG_KEYS.some(
    (sensitiveKey) => key.toLowerCase() === sensitiveKey.toLowerCase()
  ) || /token|password|authorization|secret/.test(key.toLowerCase());

export const sanitizeUrlForLog = (url = ""): string => {
  if (!url || typeof url !== "string") {
    return url;
  }

  try {
    const baseUrl =
      typeof window !== "undefined" ? window.location.origin : "http://app";
    const sanitizedUrl = new URL(url, baseUrl);

    sanitizedUrl.searchParams.forEach((_, key) => {
      if (isSensitiveKey(key)) {
        sanitizedUrl.searchParams.set(key, REDACTED_VALUE);
      }
    });

    return url.startsWith("http")
      ? sanitizedUrl.toString()
      : `${sanitizedUrl.pathname}${sanitizedUrl.search}`;
  } catch {
    return url;
  }
};

export const getSafeErrorLog = (error: ErrorHandlerError) => ({
  url: sanitizeUrlForLog(error?.config?.url),
  method: error?.config?.method,
  status: error?.response?.status,
  message: error?.response?.data?.message || error?.message,
  code: error?.code,
});

export const errorHandler = (error: ErrorHandlerError) => {
  if (error?.config?.suppressToast) {
    return;
  }

  if (axios.isCancel(error)) {
    logger.trace("Request cancelled", error.message);
    return;
  }

  if (error.response) {
    logger.error("API Error Response", getSafeErrorLog(error));

    // 401 Unauthorized is handled globally in AxiosInstance interceptor.
    // We return early here to prevent generic error toasts for session expiration.

    if (error.response.status === HTTP_STATUS.UNAUTHORIZED) {
      const message = error?.response?.data?.message;
      if (message) {
        setTimeout(() => {
          toastr.error("Error", message);
        }, 2000);
      }

      return;
    }
    if (error.response.data?.message) {
      if (error.response?.data?.message === "Invalid token specified.") {
        return;
      } else {
        toastr.error("Error", error.response.data.message);
      }
    }
  } else if (error.request) {
    logger.error("API Request Error", getSafeErrorLog(error));

    const message =
      error.message || "Something went wrong while making the request";
    toastr.error("Error", message, { id: "toast-error" });
  } else {
    logger.error("Unexpected Error", {
      error: error.message,
    });
  }
};
